/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Jan 2022
 * @flow
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Marker, InfoWindow } from '@react-google-maps/api';
import classNames from 'classnames';
import useElapsedTime from '../../../../hooks/useElapsedTime';
import Button from '../../../../components/Button';
import GoogleMap from '../../../../components/GoogleMap';
import strings from '../../../../globalization';
import classes from './ViewGuestLocation.module.css';
import { TimeConstants } from '../../../../constants';

const ViewGuestLocation = ({ onClose, check, onRequestRefresh, onOrderDelivered }) => {
  const storeInfo = useSelector(state => state.storeInfo);
  const currencyInfo = useSelector(state => state.currencyInfo);
  const { currency } = currencyInfo || {};
  const { latitude, longitude, googleApiKey } = storeInfo?.lastLocationUpdateDate || {};
  const zoom = 18;
  const mapTypeId = 'hybrid';
  const { deliverTo, number, totalAmount, orders } = check || {};
  const [order0] = orders || [];
  const { latitude: orderLat, longitude: orderLng, lastLocationUpdateDate } = order0 || {};

  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [markerPosition, setMarkerPosition] = useState({ lat: 0, lng: 0 });
  const [marker, setMarker] = useState(null);
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(true);

  const { elapsedTimeInHrMinSec, elapsedTimeInMillisecond } = useElapsedTime(lastLocationUpdateDate);
  const elapsedTimeInMin = elapsedTimeInMillisecond / TimeConstants.MINUTE;
  let refreshButtonColor = 'red';
  if (elapsedTimeInMin <= 3)
    refreshButtonColor = 'green';
  else if (elapsedTimeInMin > 3 && elapsedTimeInMin <= 6)
    refreshButtonColor = 'orange';
  else if (elapsedTimeInMin > 6)
    refreshButtonColor = 'red';

  useEffect(() => {
    setCenter({ lat: latitude, lng: longitude });
  }, [latitude, longitude]);

  useEffect(() => {
    setMarkerPosition({ lat: orderLat, lng: orderLng });
  }, [orderLng, orderLng]);

  const onMarkerLoad = useCallback(marker => {
    setMarker(marker);
  }, []);

  const closeInfoWindow = () => {
    setIsInfoWindowOpen(false);
  };

  const openInfoWindow = () => {
    setIsInfoWindowOpen(true);
  };

  const onClickRefresh = () => {
    onRequestRefresh && onRequestRefresh();
  };

  const onPressDelivered = () => {
    onOrderDelivered && onOrderDelivered(check);
  };

  return (
    <div className={classes.container}>
      <Header
        deliverTo={deliverTo}
        checkNumber={number}
        totalAmount={totalAmount}
        elapsedTime={elapsedTimeInHrMinSec}
        currency={currency}
        onClickRefresh={onClickRefresh}
        onClose={onClose}
        refreshButtonColor={refreshButtonColor}
      />
      <div className={classes.bodyWrapper}>
        <GoogleMap googleApiKey={googleApiKey} center={markerPosition} zoom={zoom} mapTypeId={mapTypeId}>
          <Marker position={markerPosition} onLoad={onMarkerLoad} onClick={openInfoWindow}>
            {isInfoWindowOpen && (
              <InfoWindow
                anchor={marker}
                position={markerPosition}
                onCloseClick={closeInfoWindow}
              >
                <div className={classes.infoWindow}>
                  <span className={classes.infoText}>{deliverTo}</span>
                  <span className={classes.infoText}>#{number}</span>
                  <span className={classes.infoText}>{elapsedTimeInHrMinSec}</span>
                  <Button className={classes.deliveredBtn} onClick={onPressDelivered}>
                    <span className={classes.deliveredBtnTxt}>Delivered</span>
                  </Button>
                </div>
              </InfoWindow>
            )}
          </Marker>
        </GoogleMap>
      </div>
    </div>
  );
};

export default ViewGuestLocation;

ViewGuestLocation.propTypes = {
  onClose: PropTypes.func.isRequired,
  check: PropTypes.object,
  currency: PropTypes.string,
  onRequestRefresh: PropTypes.func,
};

ViewGuestLocation.defaultProps = {
  onClose: () => { },
  check: {},
  currency: '$',
  onRequestRefresh: () => { },
};

const Header = ({ deliverTo, checkNumber, totalAmount, elapsedTime, currency, onClickRefresh, onClose, refreshButtonColor }) => {
  return (
    <div className={classes.headerWrapper}>
      <span className={classNames({ [classes.headerText]: true, [classes.deliverTo]: true })}>
        {strings.deliverTo}: <span className={classes.text}>{deliverTo}</span>
      </span>
      <span className={classNames({ [classes.headerText]: true, [classes.checkNum]: true })}>
        {strings.checkNumber}: <span className={classes.text}>{checkNumber}</span>
      </span>
      <span className={classNames({ [classes.headerText]: true, [classes.amount]: true })}>
        {strings.paymentTotal}: <span className={classes.text}>{currency}{totalAmount.toFixed(2)}</span>
      </span>
      <span className={classNames({ [classes.headerText]: true, [classes.amount]: true })}>
        {strings.lastUpdated}: <span className={classes.text}>{elapsedTime}</span>
      </span>
      <Button className={classes.requestButton} onClick={onClickRefresh} style={{ backgroundColor: refreshButtonColor }}>
        <span className={classes.btnText}>{strings.requestRefresh}</span>
      </Button>
      <Button onClick={onClose} className={classes.closeButton}>
        X
      </Button>
    </div>
  );
};
